import React, { useState, useEffect } from "react";
import { RefreshCw, Loader2, CheckCircle, AlertCircle, Database, FileText, MessageSquare } from "lucide-react";
import { getSyncStatus, syncJobsNow } from "../services/tenantService";
import socketService from "../services/socketService";
import { usePermissions } from "../hooks/usePermissions";

export function SettingsSync() {
  const { canUpdateJobFilters } = usePermissions();
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({
    jobs: false,
    proposals: false,
    rooms: false,
  });
  const [error, setError] = useState(null);
  const canManualSyncJobs = canUpdateJobFilters();

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const response = await getSyncStatus();
      if (response?.success && response?.data) {
        setSyncStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
      setError('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual sync for jobs
  const handleSyncJobs = async () => {
    if (!canManualSyncJobs) {
      setError("You do not have permission to trigger job sync. Ask an admin for job filters update access.");
      return;
    }

    setSyncing(prev => ({ ...prev, jobs: true }));
    setError(null);
    try {
      const response = await syncJobsNow({ immediate: true, maxPages: 100 });
      if (!response?.success) {
        throw new Error(response?.message || 'Sync failed');
      }
      // Status will update via Socket.IO event
    } catch (err) {
      console.error('Manual sync failed:', err);
      setError(err.message || 'Failed to trigger sync');
      setSyncing(prev => ({ ...prev, jobs: false }));
    }
  };

  // Set up Socket.IO listeners and initial fetch
  useEffect(() => {
    fetchSyncStatus();

    // Polling fallback every 30 seconds
    const pollInterval = setInterval(fetchSyncStatus, 30000);

    // Socket.IO event listeners
    const handleJobsStarted = () => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: true,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, jobs: true }));
    };

    const handleJobsCompleted = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: false,
          lastSyncTime: new Date().toISOString(),
          lastSyncResult: data.result,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, jobs: false }));
    };

    const handleJobsFailed = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: false,
          lastError: data.error,
          lastSyncTime: new Date().toISOString()
        }
      }));
      setSyncing(prev => ({ ...prev, jobs: false }));
      setError(data.error);
    };

    const handleProposalsStarted = () => {
      setSyncStatus(prev => ({
        ...prev,
        proposals: {
          ...prev?.proposals,
          isSyncing: true,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, proposals: true }));
    };

    const handleProposalsCompleted = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        proposals: {
          ...prev?.proposals,
          isSyncing: false,
          lastSyncTime: new Date().toISOString(),
          lastSyncResult: data.result,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, proposals: false }));
    };

    const handleProposalsFailed = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        proposals: {
          ...prev?.proposals,
          isSyncing: false,
          lastError: data.error,
          lastSyncTime: new Date().toISOString()
        }
      }));
      setSyncing(prev => ({ ...prev, proposals: false }));
    };

    const handleRoomsStarted = () => {
      setSyncStatus(prev => ({
        ...prev,
        rooms: {
          ...prev?.rooms,
          isSyncing: true,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, rooms: true }));
    };

    const handleRoomsCompleted = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        rooms: {
          ...prev?.rooms,
          isSyncing: false,
          lastSyncTime: new Date().toISOString(),
          lastSyncResult: data.result,
          lastError: null
        }
      }));
      setSyncing(prev => ({ ...prev, rooms: false }));
    };

    const handleRoomsFailed = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        rooms: {
          ...prev?.rooms,
          isSyncing: false,
          lastError: data.error,
          lastSyncTime: new Date().toISOString()
        }
      }));
      setSyncing(prev => ({ ...prev, rooms: false }));
    };

    // Connect socket if needed
    if (!socketService.isConnected()) {
      socketService.connect();
    }

    socketService.on('sync:jobs_started', handleJobsStarted);
    socketService.on('sync:jobs_completed', handleJobsCompleted);
    socketService.on('sync:jobs_failed', handleJobsFailed);
    socketService.on('sync:proposals_started', handleProposalsStarted);
    socketService.on('sync:proposals_completed', handleProposalsCompleted);
    socketService.on('sync:proposals_failed', handleProposalsFailed);
    socketService.on('sync:rooms_started', handleRoomsStarted);
    socketService.on('sync:rooms_completed', handleRoomsCompleted);
    socketService.on('sync:rooms_failed', handleRoomsFailed);

    return () => {
      clearInterval(pollInterval);
      socketService.off('sync:jobs_started', handleJobsStarted);
      socketService.off('sync:jobs_completed', handleJobsCompleted);
      socketService.off('sync:jobs_failed', handleJobsFailed);
      socketService.off('sync:proposals_started', handleProposalsStarted);
      socketService.off('sync:proposals_completed', handleProposalsCompleted);
      socketService.off('sync:proposals_failed', handleProposalsFailed);
      socketService.off('sync:rooms_started', handleRoomsStarted);
      socketService.off('sync:rooms_completed', handleRoomsCompleted);
      socketService.off('sync:rooms_failed', handleRoomsFailed);
    };
  }, []);

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return 'Never';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Render sync card
  const renderSyncCard = ({ type, title, description, icon: Icon, data, canSync }) => {
    const isSyncing = data?.isSyncing || syncing[type];
    const lastError = data?.lastError;
    const lastSyncTime = data?.lastSyncTime;
    const lastResult = data?.lastSyncResult;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Icon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            </div>
          </div>
          {canSync && (
            <button
              onClick={type === 'jobs' ? handleSyncJobs : undefined}
              disabled={isSyncing || !canSync}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-3.5 w-3.5" />
                  Sync Now
                </>
              )}
            </button>
          )}
        </div>

        {type === 'jobs' && !canSync && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-xs text-yellow-800">
              Manual sync requires `job_filters:update` permission.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {/* Status */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Status:</span>
            <div className="flex items-center gap-1.5">
              {lastError ? (
                <>
                  <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                  <span className="text-red-600 font-medium">Error</span>
                </>
              ) : isSyncing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" />
                  <span className="text-blue-600 font-medium">Syncing</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-green-600 font-medium">Ready</span>
                </>
              )}
            </div>
          </div>

          {/* Last Sync */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Last Sync:</span>
            <span className="text-gray-900 font-medium">{getTimeAgo(lastSyncTime)}</span>
          </div>

          {/* Last Result */}
          {lastResult && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Last Result:</span>
              <span className="text-gray-900 font-medium">
                {type === 'jobs' && `${lastResult.new || 0} new, ${lastResult.updated || 0} updated`}
                {type === 'proposals' && `${lastResult.created || 0} created, ${lastResult.updated || 0} updated`}
                {type === 'rooms' && `${lastResult.processed || 0} processed`}
              </span>
            </div>
          )}

          {/* Error Message */}
          {lastError && (
            <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-md">
              <p className="text-xs text-red-800">{lastError}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Data Synchronization</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage automatic syncing of data from Upwork. Jobs sync every 5 minutes, proposals every 5 minutes, and messages every 10 minutes.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {renderSyncCard({
          type: 'jobs',
          title: 'Job Postings',
          description: 'Sync job listings from Upwork based on your filters',
          icon: Database,
          data: syncStatus?.jobs,
          canSync: canManualSyncJobs,
        })}

        {renderSyncCard({
          type: 'proposals',
          title: 'Proposals',
          description: 'Sync your submitted proposals from Upwork',
          icon: FileText,
          data: syncStatus?.proposals,
          canSync: false, // Manual sync not implemented for proposals yet
        })}

        {renderSyncCard({
          type: 'rooms',
          title: 'Messages',
          description: 'Sync message threads and conversations',
          icon: MessageSquare,
          data: syncStatus?.rooms,
          canSync: false, // Manual sync not implemented for rooms yet
        })}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-1">
            <AlertCircle className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Automatic Syncing</p>
            <p className="text-xs leading-relaxed">
              Data is synced automatically in the background. Manual sync is useful when you want to immediately fetch the latest data from Upwork without waiting for the next scheduled sync.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsSync;
